class ProductCompare {
  constructor(section) {
    this.section = section;
    this.sectionId = section.dataset.sectionId;
    this.subscriptionDialog = document.querySelector(`#subscriptionUpsellDialog-${this.sectionId}`);
    this.oneTimeDialog = document.querySelector(`#oneTimePurchaseDialog-${this.sectionId}`);
    this.countdownTimers = {};
    
    this.initializeQuantitySelectors();
    this.initializeVariantSelectors();
    this.initializeBuyButtons();
    this.initializeCountdown();
    this.initializeQuantityDiscountTooltips();
    this.initializeDialogs();
  }

  // Add subscription to cart (clears cart first)
  async addSubscriptionToCart(variantId, quantity, sellingPlanId, options = {}) {
    const { button = null, successCallback = null, errorCallback = null } = options;

    // Show loading state
    let originalButtonText = '';
    if (button) {
      originalButtonText = button.innerHTML;
      button.innerHTML = `
        <div class="spinner-border spinner-border-sm" role="status">
          <span class="visually-hidden">Loading...</span>
        </div>
      `;
      button.disabled = true;
    }

    try {
      // Clear cart first
      await fetch('/cart/clear.js', { method: 'POST' });
      
      // Add subscription item
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          items: [{
            id: Number(variantId),
            quantity: quantity,
            selling_plan: sellingPlanId
          }]
        })
      });

      if (response.ok) {
        // Refresh the cart
        if (typeof loadOffcanvasCart === 'function') {
          await loadOffcanvasCart();
        } else {
          const { loadOffcanvasCart: loadCart } = await import('./cart.js');
          await loadCart();
        }

        if (successCallback) {
          successCallback();
        }
      } else {
        throw new Error('Failed to add subscription to cart');
      }
    } catch (err) {
      console.error('Error adding subscription to cart:', err);
      
      if (button) {
        button.textContent = 'Error - Try Again';
        button.classList.add('btn-danger');
        
        setTimeout(() => {
          button.innerHTML = originalButtonText;
          button.classList.remove('btn-danger');
          button.disabled = false;
        }, 2000);
      }

      if (errorCallback) {
        errorCallback(err);
      }
    }
  }

  // Reusable add to cart function
  async addToCart(formData, options = {}) {
    const {
      button = null,
      successText = 'Added!',
      successCallback = null,
      errorCallback = null,
      redirectAfterAdd = null
    } = options;

    // Show loading state
    let originalButtonText = '';
    if (button) {
      originalButtonText = button.innerHTML;
      button.innerHTML = `
        <div class="spinner-border spinner-border-sm" role="status">
          <span class="visually-hidden">Loading...</span>
        </div>
      `;
      button.disabled = true;
    }

    try {
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        body: formData
      });

      if (response.ok) {
        // Refresh the cart using the theme's cart refresh function
        if (typeof loadOffcanvasCart === 'function') {
          await loadOffcanvasCart();
        } else {
          // Fallback to importing and calling the function
          const { loadOffcanvasCart: loadCart } = await import('./cart.js');
          await loadCart();
        }

        // Handle redirect
        if (redirectAfterAdd) {
          window.location.href = redirectAfterAdd;
          return;
        }

        // Show success state
        if (button) {
          button.innerHTML = successText;
          button.classList.add('btn-success');
          
          setTimeout(() => {
            button.innerHTML = originalButtonText;
            button.classList.remove('btn-success');
            button.disabled = false;
          }, 2000);
        }

        // Call success callback if provided
        if (successCallback) {
          successCallback();
        }

      } else {
        throw new Error('Failed to add to cart');
      }
    } catch (err) {
      console.error('Error adding to cart:', err);
      
      if (button) {
        button.innerHTML = 'Try Again';
        button.classList.add('btn-danger');
        
        setTimeout(() => {
          button.innerHTML = originalButtonText;
          button.classList.remove('btn-danger');
          button.disabled = false;
        }, 2000);
      }

      // Call error callback if provided
      if (errorCallback) {
        errorCallback(err);
      }
    }
  }

  // Helper function to trigger animations
  triggerAnimation(element, animationType) {
    if (!element || animationType === 'none') return;
    
    // Remove any existing animation classes
    element.classList.remove('animate-wiggle', 'animate-shake', 'animate-pulse', 'animate-bounce', 'animate-flash', 'animate-slot-machine');
    
    // Add the new animation class
    element.classList.add(`animate-${animationType}`);
    
    // Remove animation class after animation completes
    setTimeout(() => {
      element.classList.remove(`animate-${animationType}`);
    }, 800); // Match the longest animation duration
  }

  // Update button text with dynamic savings shortcodes
  updateButtonText(item, discountPercent, dollarSavings, previousDiscountPercent = 0) {
    const button = item.querySelector('.btn-atc');
    if (!button) return;
    
    const dynamicButtonText = button.getAttribute('data-dynamic-button-text');
    if (!dynamicButtonText || discountPercent === 0) {
      // Reset to original text if no discount or no dynamic text set
      const originalText = button.getAttribute('data-text-add-to-cart') || 'Add to Cart';
      button.innerHTML = originalText;
      return;
    }
    
    // Helper function to format price (hide .00 if no cents)
    const formatPrice = (price) => {
      return price % 1 === 0 ? `$${price.toFixed(0)}` : `$${price.toFixed(2)}`;
    };
    
    // Format the values
    const formattedDollarSavings = formatPrice(dollarSavings);
    const formattedDiscountPercent = `${discountPercent}%`;
    
    // Replace shortcodes with actual values, wrapping the values in spans for animation
    let buttonText = dynamicButtonText
      .replace(/\[\$\]/g, `<span class="animated-value">${formattedDollarSavings}</span>`)
      .replace(/\[%\]/g, `<span class="animated-value">${formattedDiscountPercent}</span>`);
    
    button.innerHTML = buttonText;
    
    // Trigger animation on the specific value spans if discount percentage changed and animation is enabled
    const card = item.querySelector('.card');
    const buttonAnimation = card?.dataset.buttonAnimation || 'none';
    if (discountPercent !== previousDiscountPercent && buttonAnimation !== 'none') {
      const animatedValues = button.querySelectorAll('.animated-value');
      animatedValues.forEach(span => {
        this.triggerAnimation(span, buttonAnimation);
      });
    }
  }

  // Parse quantity discounts helper function (now percentage-based)
  parseQuantityDiscounts(discountString) {
    if (!discountString) return null;
    
    const discounts = {};
    const pairs = discountString.split(',');
    
    pairs.forEach(pair => {
      const [qty, discount] = pair.trim().split(':');
      if (qty && discount) {
        const quantity = parseInt(qty);
        const discountPercent = parseFloat(discount);
        
        if (!isNaN(quantity) && !isNaN(discountPercent) && quantity > 0 && discountPercent >= 0 && discountPercent <= 100) {
          discounts[quantity] = discountPercent;
        } else {
          console.warn('Invalid quantity discount format (should be qty:percent):', pair);
        }
      }
    });
    
    return Object.keys(discounts).length > 0 ? discounts : null;
  }

  // Initialize quantity selectors
  initializeQuantitySelectors() {
    const items = this.section.querySelectorAll('.product-compare-pricing-item');
    
    items.forEach(item => {
      const qtyMinus = item.querySelector('.qty-minus');
      const qtyPlus = item.querySelector('.qty-plus');
      const qtyInput = item.querySelector('.qty-input');
      const qtyValue = item.querySelector('.qty-value');
      const priceElement = item.querySelector('.product-compare-price');
      const buttonEl = item.querySelector('.btn-atc');
      
      // Extract base price
      const extractPrice = (priceText) => {
        const matches = priceText.match(/\$?([\d,]+\.?\d*)/g);
        if (matches && matches.length > 0) {
          const lastPrice = matches[matches.length - 1];
          return parseFloat(lastPrice.replace(/[$,]/g, ''));
        }
        return null;
      };
      
      let basePrice = extractPrice(priceElement.textContent);
      // Fallback to data-base-price (cents) if needed
      if ((basePrice === null || isNaN(basePrice)) && buttonEl?.dataset.basePrice) {
        const cents = parseInt(buttonEl.dataset.basePrice, 10);
        if (!isNaN(cents)) {
          basePrice = cents / 100;
        }
      }
      
      // Get quantity discounts and auto quantity setting
      const card = item.querySelector('.card');
      const quantityDiscounts = this.parseQuantityDiscounts(card?.dataset.quantityDiscount);
      const autoQuantity = parseInt(qtyInput.value) || 1; // Get the auto-set quantity from input
      
      // Get price per unit settings
      const enablePricePerUnit = card?.dataset.enablePricePerUnit === 'true';
      const unitsPerPackage = parseInt(card?.dataset.unitsPerPackage) || 0;
      const unitLabel = card?.dataset.unitLabel || 'unit';
      const pricePerUnitElement = item.querySelector('.price-per-unit-value');
      
      // Get animation settings
      const buttonAnimation = card?.dataset.buttonAnimation || 'none';
      const pricePerUnitAnimation = card?.dataset.pricePerUnitAnimation || 'none';
      
      // Track previous values for animation triggers
      let previousDiscountPercent = 0;
      let previousPricePerUnit = null; // null for first-run detection
      
      const updateQuantity = (newQty) => {
        qtyInput.value = newQty;
        if (qtyValue) qtyValue.value = newQty;
        
        // Update price display with percentage-based discounts
        if (basePrice) {
          const originalTotal = basePrice * newQty;
          let finalPrice = originalTotal;
          let discountPercent = 0;
          
          // Find applicable discount percentage
          if (quantityDiscounts) {
            const applicableQty = Object.keys(quantityDiscounts)
              .map(Number)
              .filter(qty => newQty >= qty)
              .sort((a, b) => b - a)[0]; // Get highest applicable quantity
              
            if (applicableQty) {
              discountPercent = quantityDiscounts[applicableQty];
              finalPrice = originalTotal * (1 - discountPercent / 100);
            }
          }
          
          // Helper function to format price (hide .00 if no cents)
          const formatPrice = (price) => {
            return price % 1 === 0 ? `$${price.toFixed(0)}` : `$${price.toFixed(2)}`;
          };
          
          // Update price display
          if (discountPercent > 0) {
            // Show original price crossed out and discounted price in green
            priceElement.innerHTML = `<s>${formatPrice(originalTotal)}</s> <span class="text-success">${formatPrice(finalPrice)}</span>`;
            priceElement.classList.add('text-success');
            setTimeout(() => {
              priceElement.classList.remove('text-success');
            }, 1000);
          } else {
            // Show regular price
            const originalPriceHtml = priceElement.dataset.originalPrice;
            if (originalPriceHtml) {
              // Replace the price in the original HTML structure
              const priceMatch = originalPriceHtml.match(/(\$[\d,]+\.?\d*)/);
              if (priceMatch) {
                priceElement.innerHTML = originalPriceHtml.replace(priceMatch[1], formatPrice(finalPrice));
              } else {
                priceElement.innerHTML = formatPrice(finalPrice);
              }
            } else {
              priceElement.innerHTML = formatPrice(finalPrice);
            }
          }
          
          // Update button text with dynamic savings
          this.updateButtonText(item, discountPercent, originalTotal - finalPrice, previousDiscountPercent);
          
          // Update price per unit calculation
          if (enablePricePerUnit && unitsPerPackage > 0 && pricePerUnitElement) {
            const totalUnits = unitsPerPackage * newQty;
            const pricePerUnit = finalPrice / totalUnits;
            
            // Format price per unit (hide .00 if no cents)
            const formattedPricePerUnit = pricePerUnit % 1 === 0 ? 
              `$${pricePerUnit.toFixed(0)}` : 
              `$${pricePerUnit.toFixed(2)}`;
            
            pricePerUnitElement.textContent = formattedPricePerUnit;
            
            // Trigger animation if price per unit changed (skip first calculation)
            if (previousPricePerUnit !== null && pricePerUnit !== previousPricePerUnit && pricePerUnitAnimation !== 'none') {
              this.triggerAnimation(pricePerUnitElement, pricePerUnitAnimation);
            }
            
            previousPricePerUnit = pricePerUnit;
          }
          
          // Update previous discount percent for next comparison
          previousDiscountPercent = discountPercent;
        }
      };
      
      // Store original price
      if (!priceElement.dataset.originalPrice) {
        priceElement.dataset.originalPrice = priceElement.innerHTML;
      }
      
      qtyMinus?.addEventListener('click', () => {
        const currentQty = parseInt(qtyInput.value);
        if (currentQty > 1) {
          updateQuantity(currentQty - 1);
        }
      });
      
      qtyPlus?.addEventListener('click', () => {
        const currentQty = parseInt(qtyInput.value);
        if (currentQty < 99) {
          updateQuantity(currentQty + 1);
        }
      });
      
      qtyInput?.addEventListener('change', () => {
        let value = parseInt(qtyInput.value);
        if (isNaN(value) || value < 1) value = 1;
        if (value > 99) value = 99;
        updateQuantity(value);
      });
      
      // Initialize with auto quantity on page load
      updateQuantity(autoQuantity);
      // Re-run next frame to ensure dynamic button text renders on load
      requestAnimationFrame(() => {
        const safeQty = parseInt(qtyInput.value, 10) || autoQuantity || 1;
        updateQuantity(safeQty);
      });
    });
  }

  // Initialize variant selectors
  initializeVariantSelectors() {
    const items = this.section.querySelectorAll('.product-compare-pricing-item');
    
    items.forEach(item => {
      const card = item.querySelector('.card');
      const enableVariantSelector = card.dataset.enableVariantSelector === 'true';
      
      if (!enableVariantSelector) return;
      
      const variantSelectors = item.querySelectorAll('.variant-option-select');
      const hiddenVariantInput = item.querySelector('input[name="id"]');
      const button = item.querySelector('.btn-atc');
      
      if (variantSelectors.length === 0 || !hiddenVariantInput) return;
      
      // Parse product variants
      let productVariants = [];
      try {
        productVariants = JSON.parse(card.dataset.productVariants);
      } catch (e) {
        console.error('Failed to parse product variants:', e);
        return;
      }
      
      // Function to find variant based on selected options
      const findVariant = () => {
        const selectedOptions = {};
        variantSelectors.forEach(select => {
          const optionName = select.dataset.optionName;
          selectedOptions[optionName] = select.value;
        });
        
        return productVariants.find(variant => {
          return variant.options.every((option, index) => {
            const optionName = Object.keys(selectedOptions)[index];
            return selectedOptions[optionName] === option;
          });
        });
      };
      
      // Function to update variant
      const updateVariant = () => {
        const selectedVariant = findVariant();
        
        if (selectedVariant) {
          // Update hidden input
          hiddenVariantInput.value = selectedVariant.id;
          
          // Update button data
          button.setAttribute('data-variant-id', selectedVariant.id);
          
          // Update price if variant has different price
          const priceElement = item.querySelector('.product-compare-price');
          if (priceElement && selectedVariant.price) {
            // You might want to update the price display here
            // This depends on how you want to handle variant pricing
          }
          
          // Check availability
          if (!selectedVariant.available) {
            button.disabled = true;
            button.textContent = 'Sold Out';
            button.classList.add('btn-secondary');
            button.classList.remove('btn-primary');
          } else {
            button.disabled = false;
            const originalText = button.getAttribute('data-text-add-to-cart') || 'Add to Cart';
            button.textContent = originalText;
            button.classList.remove('btn-secondary');
            button.classList.add('btn-primary');
          }
        }
      };
      
      // Add event listeners to variant selectors
      variantSelectors.forEach(select => {
        select.addEventListener('change', updateVariant);
      });
      
      // Initialize with first variant
      updateVariant();
    });
  }

  // Initialize buy buttons
  initializeBuyButtons() {
    const buttons = this.section.querySelectorAll('.btn-atc');
    
    buttons.forEach((button) => {
      button.addEventListener('click', async (e) => {
        e.preventDefault();
        
        const variantId = button.getAttribute('data-variant-id');
        const sellingPlanId = button.getAttribute('data-selling-plan-id');
        const action = button.getAttribute('data-action');
        
        // Get quantity
        const qtyInput = button.closest('.card').querySelector('.qty-input');
        const quantity = qtyInput ? parseInt(qtyInput.value) : 1;
        

        
        // Check if this is a non-subscription product and upsell is enabled
        const card = button.closest('.card');
        const blockId = button.closest('form').getAttribute('data-block-id');
        const enableUpsell = card.dataset.enableUpsell === 'true';
        
        if (!sellingPlanId && enableUpsell) {
          this.showUpsellModal(blockId, variantId, quantity, button);
          return;
        }
        
        // Handle one-time purchase actions
        if (!sellingPlanId && !enableUpsell) {
          const oneTimeAction = card.dataset.oneTimePurchaseAction || 'cart';
          
          if (oneTimeAction === 'checkout') {
            await this.addToCartAndRedirect(button, quantity, '/checkout');
            return;
          } else if (oneTimeAction === 'modal') {
            this.showOneTimePurchaseModal(button, quantity);
            return;
          }
        }

        // Show spinner
        const originalText = button.innerHTML;
        button.innerHTML = `
          <div class="spinner-border spinner-border-sm" role="status">
            <span class="visually-hidden">Loading...</span>
          </div>
        `;
        button.disabled = true;

        try {
          if (action === 'checkout' && sellingPlanId) {
            // For selling plans - clear cart and checkout
            button.innerHTML = originalText;
            button.disabled = false;
            
            await this.addSubscriptionToCart(variantId, quantity, sellingPlanId, {
              button: button,
              successCallback: () => {
                window.location.href = '/checkout';
              }
            });
          } else {
            // Regular add to cart
            const form = button.closest('form');
            const qtyInput = form.querySelector('[name="quantity"]');
            if (qtyInput) {
              qtyInput.value = quantity;
            }
            
            const syntheticEvent = new Event('submit', { bubbles: true, cancelable: true });
            
            if (typeof window.onSubmitAtcForm === 'function') {
              button.innerHTML = originalText;
              button.disabled = false;
              await window.onSubmitAtcForm(form, syntheticEvent);
            } else {
              // Fallback - use our reusable add to cart function
              const formData = new FormData(form);
              formData.set('quantity', quantity);
              
              button.innerHTML = originalText;
              button.disabled = false;
              
              await this.addToCart(formData, {
                button: button,
                successText: 'Added!'
              });
            }
          }
        } catch (err) {
          console.error('Error during checkout:', err);
          button.innerHTML = 'Try Again';
          button.classList.add('btn-danger');
          
          setTimeout(() => {
            button.innerHTML = originalText;
            button.classList.remove('btn-danger');
            button.disabled = false;
          }, 2000);
        }
      });
    });
  }

  // Initialize countdown timers
  initializeCountdown() {
    const formatTime = (seconds, element) => {
      const hours = Math.floor(seconds / 3600);
      const minutes = Math.floor((seconds % 3600) / 60);
      const secs = seconds % 60;
      
      const hoursText = element.dataset.textH || 'hr';
      const minutesText = element.dataset.textM || 'min';
      const secondsText = element.dataset.textS || 'sec';
      
      return {
        hours: hours > 0 ? `${hours}${hoursText}` : '',
        minutes: `${String(minutes).padStart(2, '0')}${minutesText}`,
        seconds: `${String(secs).padStart(2, '0')}${secondsText}`
      };
    };
    
    const updateCountdown = () => {
      const countdownElements = this.section.querySelectorAll('.countdown-timer-inline, .countdown-timer, .countdown-hrs, .countdown-mins, .countdown-secs');
      
      countdownElements.forEach(element => {
        const endTime = parseInt(element.dataset.time);
        const now = Math.floor(Date.now() / 1000);
        const secondsLeft = Math.max(0, endTime - now);
        
        if (secondsLeft === 0 && element.dataset.expired !== 'true') {
          element.dataset.expired = 'true';
          setTimeout(() => window.location.reload(), 1000);
        }
        
        const hours = Math.floor(secondsLeft / 3600);
        const minutes = Math.floor((secondsLeft % 3600) / 60);
        const secs = secondsLeft % 60;
        
        // Handle individual time unit elements
        if (element.classList.contains('countdown-hrs')) {
          const hoursText = element.dataset.textH || 'hr';
          element.textContent = hours > 0 ? `${hours}${hoursText}` : `0${hoursText}`;
        } else if (element.classList.contains('countdown-mins')) {
          const minutesText = element.dataset.textM || 'min';
          element.textContent = `${String(minutes).padStart(2, '0')}${minutesText}`;
        } else if (element.classList.contains('countdown-secs')) {
          const secondsText = element.dataset.textS || 'sec';
          element.textContent = `${String(secs).padStart(2, '0')}${secondsText}`;
        } else {
          // Handle combined countdown elements
          const time = formatTime(secondsLeft, element);
          
          const hoursEl = element.querySelector('[data-hours]');
          const minutesEl = element.querySelector('[data-minutes]');
          const secondsEl = element.querySelector('[data-seconds]');
          
          if (hoursEl) hoursEl.textContent = time.hours;
          if (minutesEl) minutesEl.textContent = time.minutes;
          if (secondsEl) secondsEl.textContent = time.seconds;
        }
      });
    };

    // Check if there are countdown elements in this section
    if (this.section.querySelector('.countdown-timer-inline, .countdown-timer, .countdown-hrs, .countdown-mins, .countdown-secs')) {
      updateCountdown();
      // Store timer reference specific to this section
      if (this.countdownTimer) {
        clearInterval(this.countdownTimer);
      }
      this.countdownTimer = setInterval(updateCountdown, 1000);
    }
  }

  // Initialize quantity discount tooltips
  initializeQuantityDiscountTooltips() {
    const items = this.section.querySelectorAll('.product-compare-pricing-item');
    
    items.forEach(item => {
      const card = item.querySelector('.card');
      const qtyWrapper = item.querySelector('.product-compare-qty-wrapper');
      const quantityDiscounts = this.parseQuantityDiscounts(card?.dataset.quantityDiscount);
      
      if (quantityDiscounts && Object.keys(quantityDiscounts).length > 0) {
        const tooltipContent = Object.entries(quantityDiscounts)
          .sort(([a], [b]) => parseInt(a) - parseInt(b))
          .map(([qty, discountPercent]) => `${qty}+: ${discountPercent}% off`)
          .join('<br>');
        
        qtyWrapper.setAttribute('data-bs-toggle', 'tooltip');
        qtyWrapper.setAttribute('data-bs-html', 'true');
        qtyWrapper.setAttribute('data-bs-placement', 'top');
        qtyWrapper.setAttribute('title', `Bulk Pricing:<br>${tooltipContent}`);
        
        if (typeof bootstrap !== 'undefined' && bootstrap.Tooltip) {
          new bootstrap.Tooltip(qtyWrapper);
        }
      }
    });
  }

  // Initialize dialogs
  initializeDialogs() {
    if (!('showModal' in document.createElement('dialog'))) {
      console.warn('Dialog element not supported in this browser');
      return;
    }

    // Initialize close buttons for both dialogs
    if (this.subscriptionDialog) {
      const closeBtn = this.subscriptionDialog.querySelector('.btn-close');
      closeBtn?.addEventListener('click', () => this.subscriptionDialog.close());
      
      this.subscriptionDialog.addEventListener('click', (e) => {
        if (e.target === this.subscriptionDialog) {
          this.subscriptionDialog.close();
        }
      });
    }

    if (this.oneTimeDialog) {
      const closeBtn = this.oneTimeDialog.querySelector('.btn-close');
      closeBtn?.addEventListener('click', () => this.oneTimeDialog.close());
      
      this.oneTimeDialog.addEventListener('click', (e) => {
        if (e.target === this.oneTimeDialog) {
          this.oneTimeDialog.close();
        }
      });
    }
  }

  // Find subscription block
  findSubscriptionBlock(productBlockId) {
    const blocks = this.section.querySelectorAll('.product-compare-pricing-item');
    
    for (const block of blocks) {
      const button = block.querySelector('.btn-atc');
      const sellingPlanId = button?.getAttribute('data-selling-plan-id');
      
      if (sellingPlanId && sellingPlanId.trim() !== '') {
        return block;
      }
    }
    
    return null;
  }

  // Show upsell modal
  showUpsellModal(blockId, variantId, quantity, originalButton) {
    const subscriptionBlock = this.findSubscriptionBlock(blockId);
    
    if (!subscriptionBlock || !this.subscriptionDialog) {
      console.warn('No subscription block or dialog found for upsell');
      
      // Fallback: try to find dialogs again in case they were added after initialization
      if (!this.subscriptionDialog) {
        this.subscriptionDialog = document.querySelector(`#subscriptionUpsellDialog-${this.sectionId}`);
      }
      if (!this.oneTimeDialog) {
        this.oneTimeDialog = document.querySelector(`#oneTimePurchaseDialog-${this.sectionId}`);
      }
      
      if (!subscriptionBlock || !this.subscriptionDialog) {
        return;
      }
    }
    
    const dialogTitle = this.subscriptionDialog.querySelector('.dialog-title');
    const dialogContent = this.subscriptionDialog.querySelector('.dialog-content-text');
    const discountPreview = this.subscriptionDialog.querySelector('.quantity-discounts-preview');
    const discountList = this.subscriptionDialog.querySelector('.discount-list');
    const subscribeButton = this.subscriptionDialog.querySelector('.subscribe-button');
    const continueButton = this.subscriptionDialog.querySelector('.continue-button');
    
    // Update dialog content
    const subscriptionCard = subscriptionBlock.querySelector('.card');
    
    dialogTitle.textContent = subscriptionCard.dataset.upsellModalTitle || 'Save with Subscription!';
    dialogContent.innerHTML = subscriptionCard.dataset.upsellModalContent || '<p>Subscribe and save! Get this product delivered regularly with exclusive subscriber benefits.</p>';
    
    // Show/hide quantity discounts
    const showDiscounts = subscriptionCard.dataset.showQuantityDiscountsInModal === 'true';
    const quantityDiscounts = this.parseQuantityDiscounts(subscriptionCard.dataset.quantityDiscount);
    
    if (showDiscounts && quantityDiscounts && Object.keys(quantityDiscounts).length > 0) {
      discountPreview.style.display = 'block';
      discountList.innerHTML = '';
      
      Object.entries(quantityDiscounts)
        .sort(([a], [b]) => parseInt(a) - parseInt(b))
        .forEach(([qty, discountPercent]) => {
          const discountItem = document.createElement('div');
          discountItem.className = 'discount-item';
          discountItem.innerHTML = `
            <span class="qty">${qty}+ items</span>
            <span class="price">${discountPercent}% off</span>
          `;
          discountList.appendChild(discountItem);
        });
    } else {
      discountPreview.style.display = 'none';
    }
    
    // Update button texts
    subscribeButton.textContent = subscriptionCard.dataset.upsellButtonText || 'Subscribe & Save';
    continueButton.textContent = subscriptionCard.dataset.continueButtonText || 'Continue with One-time Purchase';
    
    // Handle subscribe button click
    const newSubscribeButton = subscribeButton.cloneNode(true);
    subscribeButton.parentNode.replaceChild(newSubscribeButton, subscribeButton);
    
    newSubscribeButton.addEventListener('click', async () => {
      const sellingPlanId = subscriptionBlock.querySelector('.btn-atc').getAttribute('data-selling-plan-id');
      
      await this.addSubscriptionToCart(variantId, quantity, sellingPlanId, {
        button: newSubscribeButton,
        successCallback: () => {
          this.subscriptionDialog.close();
          window.location.href = '/checkout';
        },
        errorCallback: () => {
          setTimeout(() => {
            newSubscribeButton.textContent = subscriptionCard.dataset.upsellButtonText || 'Subscribe & Save';
            newSubscribeButton.classList.remove('btn-danger');
            newSubscribeButton.disabled = false;
          }, 2000);
        }
      });
    });
    
    // Handle continue button click
    const newContinueButton = continueButton.cloneNode(true);
    continueButton.parentNode.replaceChild(newContinueButton, continueButton);
    
    newContinueButton.addEventListener('click', async () => {
      const originalCard = originalButton.closest('.card');
      const oneTimeAction = originalCard.dataset.oneTimePurchaseAction || 'cart';
      
      if (oneTimeAction === 'modal') {
        this.showOneTimePurchaseModal(originalButton, quantity, originalCard);
        this.subscriptionDialog.close();
        return;
      }
      
      if (oneTimeAction === 'checkout') {
        await this.addToCartAndRedirectFromModal(originalButton, quantity, '/checkout', newContinueButton);
        this.subscriptionDialog.close();
        return;
      }
      
      // Default: add to cart
      newContinueButton.disabled = true;
      newContinueButton.innerHTML = `
        <div class="spinner-border spinner-border-sm" role="status">
          <span class="visually-hidden">Loading...</span>
        </div>
      `;
      
      try {
        const form = originalButton.closest('form');
        const qtyInput = form.querySelector('[name="quantity"]');
        if (qtyInput) {
          qtyInput.value = quantity;
        }
        
        this.subscriptionDialog.close();
        
        newContinueButton.innerHTML = subscriptionCard.dataset.continueButtonText || 'Continue with One-time Purchase';
        newContinueButton.disabled = false;
        
        const syntheticEvent = new Event('submit', { bubbles: true, cancelable: true });
        
        if (typeof window.onSubmitAtcForm === 'function') {
          await window.onSubmitAtcForm(form, syntheticEvent);
        } else {
          const formData = new FormData(form);
          formData.set('quantity', quantity);
          
          newContinueButton.innerHTML = subscriptionCard.dataset.continueButtonText || 'Continue with One-time Purchase';
          newContinueButton.disabled = false;
          
          await this.addToCart(formData, {
            button: originalButton,
            successText: 'Added!'
          });
        }
      } catch (err) {
        console.error('Error adding to cart:', err);
        newContinueButton.textContent = 'Error - Try Again';
        newContinueButton.classList.add('btn-danger');
        
        setTimeout(() => {
          newContinueButton.textContent = subscriptionCard.dataset.continueButtonText || 'Continue with One-time Purchase';
          newContinueButton.classList.remove('btn-danger');
          newContinueButton.disabled = false;
        }, 2000);
      }
    });
    
    this.subscriptionDialog.showModal();
  }

  // Show one-time purchase modal
  showOneTimePurchaseModal(originalButton, quantity, settingsCard = null) {
    const card = originalButton.closest('.card');
    const configCard = settingsCard || card;
    
    if (!this.oneTimeDialog) {
      console.error('One-time purchase dialog not found!');
      
      // Fallback: try to find dialog again in case it was added after initialization
      this.oneTimeDialog = document.querySelector(`#oneTimePurchaseDialog-${this.sectionId}`);
      
      if (!this.oneTimeDialog) {
        return;
      }
    }
    
    const dialogTitle = this.oneTimeDialog.querySelector('.dialog-title');
    const dialogContent = this.oneTimeDialog.querySelector('.dialog-content-text');
    const confirmButton = this.oneTimeDialog.querySelector('.confirm-button');
    const cancelButton = this.oneTimeDialog.querySelector('.cancel-button');
    
    // Update dialog content
    dialogTitle.textContent = configCard.dataset.oneTimeModalTitle || 'One-time Purchase';
    dialogContent.innerHTML = configCard.dataset.oneTimeModalContent || '<p>You\'re about to make a one-time purchase. Would you like to continue?</p>';
    
    // Update button texts
    confirmButton.textContent = configCard.dataset.oneTimeConfirmText || 'Yes, Continue to Checkout';
    cancelButton.textContent = configCard.dataset.oneTimeCancelText || 'Back to Options';
    
    // Handle confirm button click
    const newConfirmButton = confirmButton.cloneNode(true);
    confirmButton.parentNode.replaceChild(newConfirmButton, confirmButton);
    
    newConfirmButton.addEventListener('click', async () => {
      newConfirmButton.disabled = true;
      newConfirmButton.innerHTML = `
        <div class="spinner-border spinner-border-sm" role="status">
          <span class="visually-hidden">Loading...</span>
        </div>
      `;
      
      try {
        const form = originalButton.closest('form');
        const qtyInput = form.querySelector('[name="quantity"]');
        if (qtyInput) {
          qtyInput.value = quantity;
        }
        
        const modalAction = configCard.dataset.oneTimeModalAction || 'checkout';
        const syntheticEvent = new Event('submit', { bubbles: true, cancelable: true });
        
        this.oneTimeDialog.close();
        
        newConfirmButton.innerHTML = configCard.dataset.oneTimeConfirmText || 'Yes, Continue to Checkout';
        newConfirmButton.disabled = false;
        
        if (typeof window.onSubmitAtcForm === 'function') {
          await window.onSubmitAtcForm(form, syntheticEvent);
          
          if (modalAction === 'checkout') {
            setTimeout(() => {
              window.location.href = '/checkout';
            }, 100);
          }
        } else {
          const formData = new FormData(form);
          formData.set('quantity', quantity);
          
          newConfirmButton.innerHTML = configCard.dataset.oneTimeConfirmText || 'Yes, Continue to Checkout';
          newConfirmButton.disabled = false;
          
          const redirectAfterAdd = modalAction === 'checkout' ? '/checkout' : null;
          
          await this.addToCart(formData, {
            button: originalButton,
            successText: 'Added!',
            redirectAfterAdd: redirectAfterAdd
          });
        }
      } catch (err) {
        console.error('Error adding to cart:', err);
        newConfirmButton.textContent = 'Error - Try Again';
        newConfirmButton.classList.add('btn-danger');
        
        setTimeout(() => {
          newConfirmButton.textContent = configCard.dataset.oneTimeConfirmText || 'Yes, Continue to Checkout';
          newConfirmButton.classList.remove('btn-danger');
          newConfirmButton.disabled = false;
        }, 2000);
      }
    });
    
    // Handle cancel button click
    const newCancelButton = cancelButton.cloneNode(true);
    cancelButton.parentNode.replaceChild(newCancelButton, cancelButton);
    
    newCancelButton.addEventListener('click', () => {
      this.oneTimeDialog.close();
    });
    
    this.oneTimeDialog.showModal();
  }

  // Helper: Add to cart and redirect
  async addToCartAndRedirect(button, quantity, redirectUrl) {
    const form = button.closest('form');
    const formData = new FormData(form);
    formData.set('quantity', quantity);
    
    await this.addToCart(formData, {
      button: button,
      redirectAfterAdd: redirectUrl
    });
  }

  // Helper: Add to cart and redirect from modal
  async addToCartAndRedirectFromModal(originalButton, quantity, redirectUrl, modalButton) {
    const form = originalButton.closest('form');
    const formData = new FormData(form);
    formData.set('quantity', quantity);
    
    await this.addToCart(formData, {
      button: modalButton,
      redirectAfterAdd: redirectUrl,
      errorCallback: () => {
        setTimeout(() => {
          modalButton.textContent = 'Continue with One-time Purchase';
          modalButton.classList.remove('btn-danger');
          modalButton.disabled = false;
        }, 2000);
      }
    });
  }

  // Cleanup method
  destroy() {
    if (this.countdownTimer) {
      clearInterval(this.countdownTimer);
    }
    
    // Clean up tooltips
    const tooltips = this.section.querySelectorAll('[data-bs-toggle="tooltip"]');
    tooltips.forEach(el => {
      const tooltip = bootstrap.Tooltip.getInstance(el);
      if (tooltip) {
        tooltip.dispose();
      }
    });
  }
}

// Initialize Product Compare sections
function initProductCompareSections() {
  const sections = document.querySelectorAll('.product-compare-pricing[data-section-id]');
  
  // Store instances for cleanup if needed
  window.productCompareInstances = window.productCompareInstances || new Map();
  
  sections.forEach(section => {
    const sectionId = section.dataset.sectionId;
    
    // Clean up existing instance if present
    if (window.productCompareInstances.has(sectionId)) {
      window.productCompareInstances.get(sectionId).destroy();
    }
    
    // Create new instance
    const instance = new ProductCompare(section);
    window.productCompareInstances.set(sectionId, instance);
  });
}

// Initialize on DOM ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initProductCompareSections);
} else {
  initProductCompareSections();
}

// Handle theme editor reloads
if (window.Shopify && window.Shopify.designMode) {
  document.addEventListener('shopify:section:load', function(event) {
    if (event.target.querySelector('.product-compare-pricing')) {
      initProductCompareSections();
    }
  });
  
  document.addEventListener('shopify:section:unload', function(event) {
    const section = event.target.querySelector('.product-compare-pricing[data-section-id]');
    if (section) {
      const sectionId = section.dataset.sectionId;
      if (window.productCompareInstances && window.productCompareInstances.has(sectionId)) {
        window.productCompareInstances.get(sectionId).destroy();
        window.productCompareInstances.delete(sectionId);
      }
    }
  });
} 